import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  patchStructuralAssessment,
  Report,
} from "@/services/backend/htz/report/types/report";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDatePicker,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import t from "@/lang/lang";
import {
  updateHtzReportCache,
  PatchStructuralAssessmentRequest,
  useHtzReportPatchStructuralAssessmentMutation,
  useHtzReportShowQuery,
} from "@/services/backend/htz/report/api/report-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  Result,
  ResultItems,
} from "@/services/backend/htz/inspection/components/inspection-result";
import { UUID } from "@bitory-gmbh/nidavellir";
import { AhuComponentSelect } from "@/services/backend/htz/ahu/components/ahu-component-select";
import { ReportAssessmentRemoveDialog } from "@/services/backend/htz/report/components/report-assessment-remove-dialog";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";

export function StructuralAssessmentsTable({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });

  if (!data) return;

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Komponente")}</ETableHead>
          <ETableHead>{t("Anforderung")}</ETableHead>
          <ETableHead>{t("Mangel")}</ETableHead>
          <ETableHead>{t("Handlungsempfehlung")}</ETableHead>
          <ETableHead>{t("Bemerkung")}</ETableHead>
          <ETableHead>{t("Durchgeführt am")}</ETableHead>
          <ETableHead>{t("Ergebnis")}</ETableHead>
          <ETableHead />
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {data.structuralAssessments.length === 0 && (
          <TableEmptyRow colSpan={8} />
        )}
        {data.structuralAssessments.map((assessment) => (
          <Row
            key={assessment.id}
            reportId={data.id}
            assessmentId={assessment.id}
          />
        ))}
      </ETableBody>
    </ETable>
  );
}

function Row({
  reportId,
  assessmentId,
}: {
  reportId: UUID;
  assessmentId: UUID;
}) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const fieldName = joinFieldName("structuralAssessments", assessmentId);
  const request: PatchStructuralAssessmentRequest = {
    id: reportId,
    assessmentId,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchStructuralAssessmentMutation(),
  );

  const assessment = data?.structuralAssessments.find(
    (a) => a.id === assessmentId,
  );

  if (!data || !assessment) return;

  const disabled = isImmutable(data);
  return (
    <ETableRow key={assessment.id}>
      <ETableCell className="px-0">
        <AhuComponentSelect
          ahuDataId={data.ahuDataId!}
          componentId={assessment.componentId}
          onComponentIdChange={(componentId) => {
            set(
              { ...request, componentId },
              patchStructuralAssessment(data, { ...assessment, componentId }),
            );
          }}
          fieldName={joinFieldName(fieldName, "componentId")}
          disabled={disabled}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCellInput
        value={assessment.requirement}
        onChange={(e) => {
          const requirement = e.target.value;
          set(
            { ...request, requirement },
            patchStructuralAssessment(data, { ...assessment, requirement }),
          );
        }}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "requirement")}
        // A requirement is a sentence. The cell is capped so it cannot
        // push the other columns out of view; the input scrolls, so the
        // whole text stays readable and editable.
        cellClassName="max-w-64"
        className="field-sizing-fixed w-64"
        title={assessment.requirement}
      />
      <ETableCellInput
        value={assessment.deficiency}
        onChange={(e) => {
          const deficiency = e.target.value;
          set(
            { ...request, deficiency },
            patchStructuralAssessment(data, { ...assessment, deficiency }),
          );
        }}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "deficiency")}
      />
      <ETableCellInput
        value={assessment.recommendation}
        onChange={(e) => {
          const recommendation = e.target.value;
          set(
            { ...request, recommendation },
            patchStructuralAssessment(data, { ...assessment, recommendation }),
          );
        }}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "recommendation")}
      />
      <ETableCellInput
        value={assessment.comment}
        onChange={(e) => {
          const comment = e.target.value;
          set(
            { ...request, comment },
            patchStructuralAssessment(data, { ...assessment, comment }),
          );
        }}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "comment")}
      />
      <ETableCellDatePicker
        date={assessment.conductedAt}
        onDateChange={(conductedAt) =>
          set(
            { ...request, conductedAt },
            patchStructuralAssessment(data, { ...assessment, conductedAt }),
          )
        }
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "conductedAt")}
      />
      <ETableCellSelect
        value={assessment.result}
        onValueChange={(result) => {
          set(
            { ...request, result: result as Result },
            patchStructuralAssessment(data, {
              ...assessment,
              result: result as Result,
            }),
          );
        }}
        label={t("Ergebnis")}
        items={ResultItems}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "result")}
      />
      <ETableCell className="px-0 text-right">
        {!disabled && (
          <ReportAssessmentRemoveDialog
            reportId={reportId}
            assessmentId={assessmentId}
            title={t("Konstruktive Beurteilung wirklich löschen?")}
          />
        )}
      </ETableCell>
    </ETableRow>
  );
}
